/**
 * Landing Kit Legal UGC · Legati Digital
 * 1. Aparición suave de elementos al hacer scroll.
 * 2. Barra de compra fija en mobile, visible solo cuando ningún CTA de la página está a la vista.
 * 3. Compra: pide aceptar los Términos (una vez), agrega el Kit al carrito nativo (sin duplicarlo
 *    si ya estaba), registra la aceptación en el carrito y lleva al checkout.
 * Sin dependencias. Si el script no carga, todo el contenido queda visible y el formulario
 * nativo de Shopify sigue funcionando (/cart/add → /checkout).
 */
(() => {
  if (window.kitUgcInitialized) return;
  window.kitUgcInitialized = true;

  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const supportsObserver = 'IntersectionObserver' in window;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function initReveal() {
    // En el editor de temas las secciones se re-renderizan: se muestra todo sin animar.
    if (designMode || reduceMotion || !supportsObserver) return;

    const elements = document.querySelectorAll('.kit-ugc [data-kit-reveal]');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    elements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        element.classList.add('is-visible');
      } else {
        observer.observe(element);
      }
    });

    document.documentElement.classList.add('kit-ugc-js');
  }

  function initSticky() {
    const sticky = document.querySelector('[data-kit-sticky]');
    if (!sticky || !supportsObserver) return;

    const anchors = Array.from(document.querySelectorAll('[data-kit-buy-anchor]'));
    const heroAnchor = document.querySelector('[data-kit-buy-anchor="hero"]');
    const finalAnchor = document.querySelector('[data-kit-buy-anchor="final"]');
    if (!heroAnchor) return;

    const visible = new Set();
    let ticking = false;

    const update = () => {
      ticking = false;
      const pastHero = heroAnchor.getBoundingClientRect().bottom < 0;
      const beforeFinal = finalAnchor ? finalAnchor.getBoundingClientRect().top > window.innerHeight : true;
      const shouldShow = pastHero && beforeFinal && visible.size === 0;
      sticky.classList.toggle('is-shown', shouldShow);
      sticky.inert = !shouldShow;
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      });
      update();
    });

    anchors.forEach((anchor) => observer.observe(anchor));
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );
  }

  function initBuy() {
    if (!window.fetch) return;

    const headers = { Accept: 'application/json' };
    const terms = document.querySelector('dialog[data-kit-terms]');
    const termsCheck = terms && terms.querySelector('[data-kit-terms-check]');
    const termsConfirm = terms && terms.querySelector('[data-kit-terms-confirm]');
    let acceptedAt = null;
    let pendingForm = null;

    const clearBusy = () => {
      document.querySelectorAll('.kit-ugc .kit-btn[aria-busy]').forEach((button) => button.removeAttribute('aria-busy'));
    };

    // Registra la aceptación como atributo del carrito: aparece en el pedido (Detalles adicionales).
    const recordAcceptance = () => {
      if (!acceptedAt) return Promise.resolve();
      const version = terms.getAttribute('data-kit-terms-version') || '';
      return fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { 'Términos y condiciones': `Aceptados · versión ${version} · ${acceptedAt}` },
        }),
      }).then((response) => {
        if (!response.ok) throw new Error('attributes');
      });
    };

    const checkout = (form) => {
      const button = form.querySelector('button[type="submit"]');
      if (button && button.getAttribute('aria-busy') === 'true') return;
      if (button) button.setAttribute('aria-busy', 'true');

      const id = Number(form.querySelector('[name="id"]').value);

      fetch(`${root}cart.js`, { headers })
        .then((response) => response.json())
        .then((cart) => {
          const inCart = (cart.items || []).some((item) => item.variant_id === id);
          if (inCart) return true;
          return fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
          }).then((response) => {
            if (!response.ok) throw new Error('add');
            return true;
          });
        })
        // Si el registro falla, igual se sigue al pago (el Kit ya está en el carrito; no reenviar el formulario).
        .then(() => recordAcceptance().catch(() => {}))
        .then(() => {
          window.location.href = `${root}checkout`;
        })
        .catch(() => {
          // Respaldo: flujo nativo de Shopify con return_to=/checkout.
          HTMLFormElement.prototype.submit.call(form);
        });
    };

    if (terms && termsCheck && termsConfirm && typeof terms.showModal === 'function') {
      termsCheck.addEventListener('change', () => {
        termsConfirm.disabled = !termsCheck.checked;
      });
      termsConfirm.addEventListener('click', () => {
        if (!termsCheck.checked || !pendingForm) return;
        acceptedAt = new Date().toISOString();
        const form = pendingForm;
        pendingForm = null;
        terms.close();
        checkout(form);
      });
      terms.querySelector('[data-kit-terms-cancel]').addEventListener('click', () => terms.close());
      terms.addEventListener('close', () => {
        pendingForm = null;
      });
      // Clic en el fondo oscuro: cierra.
      terms.addEventListener('click', (event) => {
        if (event.target === terms) terms.close();
      });
    }

    const needsTerms = () => Boolean(terms && termsCheck && termsConfirm && typeof terms.showModal === 'function' && !acceptedAt);

    document.addEventListener('submit', (event) => {
      const form = event.target.closest && event.target.closest('form[data-kit-buy]');
      if (!form) return;
      event.preventDefault();
      event.stopPropagation();

      if (needsTerms()) {
        pendingForm = form;
        termsCheck.checked = false;
        termsConfirm.disabled = true;
        terms.showModal();
        termsCheck.focus();
        return;
      }

      checkout(form);
    }, true);

    window.addEventListener('pageshow', clearBusy);
  }

  function initInteractions() {
    if (reduceMotion) return;

    // Botones: luz que sigue al puntero y onda en el punto donde se toca.
    document.addEventListener('pointermove', (event) => {
      const button = event.target.closest && event.target.closest('.kit-ugc .kit-btn');
      if (!button) return;
      const rect = button.getBoundingClientRect();
      button.style.setProperty('--kit-mx', `${event.clientX - rect.left}px`);
      button.style.setProperty('--kit-my', `${event.clientY - rect.top}px`);
    }, { passive: true });

    document.addEventListener('pointerdown', (event) => {
      const button = event.target.closest && event.target.closest('.kit-ugc .kit-btn');
      if (!button || button.disabled) return;
      const rect = button.getBoundingClientRect();
      const ripple = document.createElement('span');
      ripple.className = 'kit-ripple';
      ripple.style.left = `${event.clientX - rect.left}px`;
      ripple.style.top = `${event.clientY - rect.top}px`;
      button.appendChild(ripple);
      ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
    }, { passive: true });

    // Pantallas táctiles: sin hover, las tarjetas se activan al pasar por el centro de la pantalla.
    if (designMode || !supportsObserver || !window.matchMedia('(hover: none)').matches) return;
    const cards = document.querySelectorAll(
      '.kit-ugc .kit-stage, .kit-ugc .kit-duo__item, .kit-ugc .kit-rc, .kit-ugc .kit-fit li, .kit-ugc .kit-steps li, .kit-ugc .kit-band li'
    );
    if (!cards.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.target.classList.toggle('is-hot', entry.isIntersecting)),
      { rootMargin: '-38% 0px -38% 0px' }
    );
    cards.forEach((card) => observer.observe(card));
  }

  const init = () => {
    initReveal();
    initSticky();
    initBuy();
    initInteractions();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
